import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Briefcase, Shield, Monitor, Building2, BarChart3, Users, TrendingUp, Leaf, Check, BookOpen, Search, ChevronDown, X, Lock } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import CourseCover from './CourseCover';
import StatusChip from './ui/StatusChip';
import ProgressBar from './ui/ProgressBar';
import { categoryFill } from '../data/categoryColours';
import './CourseCatalogue.css';

const ACADEMIES = [
  { icon: Sparkles,   name: 'AI Academy',                     slug: 'ai',           ids: ['intro-to-ai', 'ai-microsoft-office', 'ai-reporting-analysis', 'prompt-mastery', 'mastering-claude-ai', 'claude-skills', 'claude-code', 'claude-cowork', 'claude-design', 'chatgpt-for-professionals', 'chatgpt-custom-gpts', 'chatgpt-advanced-data-analysis', 'chatgpt-canvas-projects', 'chatgpt-image-design', 'mastering-chatgpt'] },
  { icon: Briefcase,  name: 'Microsoft Academy',               slug: 'microsoft',    ids: ['sharepoint-business', 'dynamics-365-crm', 'microsoft-copilot-365', 'ai-excel-mastery', 'ai-powerpoint-mastery'] },
  { icon: Shield,     name: 'Cybersecurity Academy',           slug: 'cybersecurity',ids: ['anti-hacking-foundation'] },
  { icon: Monitor,    name: 'Developer Academy',               slug: 'developer',    ids: ['complete-github-course'] },
  { icon: Building2,  name: 'Transformation & Compliance',     slug: 'compliance',   ids: ['safe-ai-compliance'] },
  { icon: Building2,  name: 'B-BBEE Academy',                  slug: 'bbbee',         ids: ['bbbee-awareness-halfday', 'bbbee-masterclass', 'bbbee-professional-3day'] },
  { icon: Leaf,       name: 'ESG Academy',                     slug: 'esg',           ids: ['esg-foundations-reporting', 'esg-fullday-practitioner', 'esg-professional-3day'] },
  { icon: BarChart3,  name: 'Data & Analytics Academy',        slug: 'data',         ids: [] },
  { icon: Users,      name: 'HR Academy',                      slug: 'hr',           ids: ['employment-equity-training', 'employment-equity-committee', 'skills-development-wsp-atr'] },
  { icon: TrendingUp, name: 'Business Skills Academy',         slug: 'business',     ids: ['finance-for-non-finance-managers', 'credit-analysis-lending-deal-structuring'] },
  { icon: Leaf,       name: 'Leadership Academy',              slug: 'leadership',   ids: [] },
];

function Stars({ rating, count }) {
  const rounded = Math.round((rating || 0) * 2) / 2;
  return (
    <span className="stars" role="img" aria-label={rating > 0 ? `Rated ${Number(rating).toFixed(1)} out of 5${count > 0 ? `, ${count} reviews` : ''}` : 'Not yet rated'}>
      {[1,2,3,4,5].map(i => (
        <span key={i} aria-hidden="true" style={{ color: i <= rounded ? 'var(--ok-text-2)' : 'var(--ok-border)' }}>★</span>
      ))}
      <span className="stars-val" aria-hidden="true">{rating > 0 ? Number(rating).toFixed(1) : 'Not yet rated'}</span>
      {count > 0 && <span className="stars-count" aria-hidden="true">({count})</span>}
    </span>
  );
}

function InteractiveStars({ courseId, userId, existingRating, onReviewed }) {
  const [hovered,  setHovered]  = useState(0);
  const [selected, setSelected] = useState(existingRating || 0);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(!!existingRating);

  async function submitRating(star) {
    setSelected(star);
    setSaving(true);
    try {
      await fetch(`/api/courses/${courseId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, rating: star }),
      });
      setSaved(true);
      if (onReviewed) onReviewed(courseId, star);
    } catch (_) {}
    setSaving(false);
  }

  const display = hovered || selected;

  return (
    <div className="cc-review-widget">
      <span className="cc-review-label">{saved ? 'Your rating:' : 'Rate this course:'}</span>
      <span className="cc-interactive-stars" role="group" aria-label="Your rating">
        {[1,2,3,4,5].map(i => (
          <button
            key={i}
            className="cc-star-btn"
            style={{ color: i <= display ? 'var(--ok-text)' : 'var(--ok-text-muted)' }}
            aria-pressed={i === selected}
            onMouseEnter={() => !saving && setHovered(i)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => !saving && submitRating(i)}
            aria-label={`Rate ${i} star${i !== 1 ? 's' : ''}`}
            disabled={saving}
          >★</button>
        ))}
      </span>
      <span className="cc-review-saved" role="status">{saved && <><Check size={14} strokeWidth={1.75} aria-hidden="true" /> Rating saved</>}</span>
    </div>
  );
}

// Click-to-reveal filter group — collapsed by default, expands on click
// rather than always showing every option list at once.
// A filter in the toolbar above the results: a button that opens a small
// panel of options. Only one is open at a time (see openFilter below).
function FilterDropdown({ id, title, label, badge, open, onToggle, children, wide }) {
  const panelId = `cf-panel-${id}`;
  return (
    <div className={`cf-drop${open ? ' cf-drop--open' : ''}`}>
      <button type="button" className={`cf-drop-btn${badge ? ' cf-drop-btn--active' : ''}`} onClick={onToggle} aria-expanded={open} aria-controls={panelId}>
        <span>{title}</span>
        {badge ? <span className="cf-drop-badge"><span className="sr-only"> ({badge} selected)</span><span aria-hidden="true">{badge}</span></span> : null}
        <ChevronDown size={14} strokeWidth={1.75} className="cf-group-chevron" aria-hidden="true" />
      </button>
      {open && <div id={panelId} role="group" aria-label={`${label || title} filter`} className={`cf-drop-panel${wide ? ' cf-drop-panel--wide' : ''}`}>{children}</div>}
    </div>
  );
}

export default function CourseCatalogue({ user, isAdmin, enrollments, progress, enroll, navigateTo, initialSearch }) {
  const [courses,        setCourses]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [selectedCats,   setSelectedCats]   = useState(new Set());
  const [difficulty,     setDifficulty]     = useState('all');
  const [openFilter,     setOpenFilter]     = useState(null);
  const toolbarRef = useRef(null);
  const [durationBuckets,setDurationBuckets]= useState(new Set());
  const [myRatings,      setMyRatings]      = useState({});
  const [notified,       setNotified]       = useState(new Set());
  const [waitlists,      setWaitlists]      = useState([]);
  const [notifySending,  setNotifySending]  = useState(false);
  const [notifySent,     setNotifySent]     = useState({});

  useEffect(() => {
    fetch(`/api/courses${user?.id ? `?userId=${user.id}` : ''}`)
      .then(r => r.json())
      .then(j => { setCourses(j.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user?.id]);

  useEffect(() => {
    if (!initialSearch?.nonce) return;
    setSearch(initialSearch.query || '');
    setSelectedCats(new Set());
    setDifficulty('all');
    setDurationBuckets(new Set());
    requestAnimationFrame(() => toolbarRef.current?.querySelector('#cf-search-input')?.focus());
  }, [initialSearch]);

  useEffect(() => {
    if (!user?.id) return;
    if (isAdmin) {
      fetch('/api/admin/notifications')
        .then(r => r.json())
        .then(j => { if (j.success) setWaitlists(j.data); })
        .catch(() => {});
    } else {
      fetch(`/api/notify-me?userId=${user.id}`)
        .then(r => r.json())
        .then(j => { if (j.success) setNotified(new Set(j.academies)); })
        .catch(() => {});
    }
  }, [user?.id, isAdmin]);

  function handleReviewed(courseId, star) {
    setMyRatings(prev => ({ ...prev, [courseId]: star }));
    setCourses(prev => prev.map(c => {
      if (c.id !== courseId) return c;
      const wasRated = !!myRatings[courseId];
      const oldSum   = (c.rating || 0) * (c.reviews || 0);
      const newCount = wasRated ? c.reviews : (c.reviews || 0) + 1;
      const newSum   = wasRated ? oldSum - (myRatings[courseId] || 0) + star : oldSum + star;
      return { ...c, rating: Math.round((newSum / newCount) * 10) / 10, reviews: newCount };
    }));
  }

  async function sendWaitlistNotification(slug, name) {
    setNotifySending(true);
    try {
      const res  = await fetch('/api/admin/notify-waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ academy: slug, academyName: name }),
      });
      const json = await res.json();
      setNotifySent(prev => ({ ...prev, [slug]: json.sent || 0 }));
      setWaitlists(prev => prev.filter(n => n.academy !== slug));
    } finally {
      setNotifySending(false);
    }
  }

  async function toggleNotify(slug) {
    const already     = notified.has(slug);
    const method      = already ? 'DELETE' : 'POST';
    const academyName = ACADEMIES.find(a => a.slug === slug)?.name || slug;
    try {
      const res  = await fetch('/api/notify-me', {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.id, academy: slug, academyName }),
      });
      const json = await res.json();
      if (json.success) {
        setNotified(prev => {
          const next = new Set(prev);
          already ? next.delete(slug) : next.add(slug);
          return next;
        });
      }
    } catch (_) {}
  }

  function toggleGroup(key) {
    setOpenFilter(prev => (prev === key ? null : key));
  }
  function clearFilters() {
    setSearch('');
    setSelectedCats(new Set());
    setDifficulty('all');
    setDurationBuckets(new Set());
    setOpenFilter(null);
  }

  // Close an open filter panel on an outside click or Escape.
  useEffect(() => {
    if (!openFilter) return undefined;
    const onDown = e => { if (toolbarRef.current && !toolbarRef.current.contains(e.target)) setOpenFilter(null); };
    const onKey = e => { if (e.key === 'Escape') { setOpenFilter(null); toolbarRef.current?.querySelector('.cf-drop--open .cf-drop-btn')?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [openFilter]);
  function toggleCat(slug) {
    setSelectedCats(prev => {
      const next = new Set(prev);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  }
  function toggleDurationBucket(bucket) {
    setDurationBuckets(prev => {
      const next = new Set(prev);
      next.has(bucket) ? next.delete(bucket) : next.add(bucket);
      return next;
    });
  }
  function academySlugFor(courseId) {
    return ACADEMIES.find(a => a.ids.includes(courseId))?.slug;
  }
  function durationBucketFor(durationStr) {
    // "3 days" style durations count as 8 learning hours a day.
    const hours = (parseFloat(durationStr) || 0) * (/day/i.test(durationStr || '') ? 8 : 1);
    if (hours <= 2) return '0-2';
    if (hours <= 5) return '2-5';
    if (hours <= 10) return '5-10';
    return '10+';
  }

  const q = search.trim().toLowerCase();
  const visibleCourses = courses.filter(c => {
    if (selectedCats.size > 0 && !selectedCats.has(academySlugFor(c.id))) return false;
    if (difficulty !== 'all' && c.level !== difficulty) return false;
    if (durationBuckets.size > 0 && !durationBuckets.has(durationBucketFor(c.duration))) return false;
    const searchable = [c.title, c.description, c.tags, c.searchTags, c.keywords]
      .flatMap(value => Array.isArray(value) ? value : [value])
      .filter(value => typeof value === 'string')
      .join(' ')
      .toLowerCase();
    if (q && !searchable.includes(q)) return false;
    return true;
  }).sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)); // pinned/featured courses (e.g. the recommended starting course) surface first, stable otherwise

  // A single selected, still-empty academy (per its hardcoded ids: []) shows the
  // existing "coming soon" / waitlist-notify view instead of an empty grid — but
  // only when no other filter is narrowing the view, so it reads as "you picked
  // this one category" rather than "your combined filters happen to match nothing."
  const soloAcademy   = selectedCats.size === 1 ? ACADEMIES.find(a => selectedCats.has(a.slug)) : null;
  const isComingSoon  = !!soloAcademy && soloAcademy.ids.length === 0 && difficulty === 'all' && durationBuckets.size === 0 && !q;
  const waitlistRow   = soloAcademy ? waitlists.find(n => n.academy === soloAcademy.slug) : null;
  const waitlist      = waitlistRow?.waitlist || [];
  const DURATION_BUCKETS = [['0-2', '0-2 hours'], ['2-5', '2-5 hours'], ['5-10', '5-10 hours'], ['10+', '10+ hours']];

  const themeGroups = [
    ...ACADEMIES.map(a => ({ slug: a.slug, name: a.name, icon: a.icon, courses: visibleCourses.filter(c => academySlugFor(c.id) === a.slug) })),
    { slug: 'other', name: 'Other courses', icon: BookOpen, courses: visibleCourses.filter(c => !academySlugFor(c.id)) },
  ].filter(g => g.courses.length > 0);
  const jumpToTheme = slug => document.getElementById(`theme-${slug}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  if (loading) return <LoadingSpinner message="Loading courses…" />;

  return (
    <div className="catalogue">
      <div className="catalogue-toolbar" ref={toolbarRef}>
        <div className="cf-search" role="search">
          <label htmlFor="cf-search-input" className="sr-only">Search courses by title or description</label>
          <Search size={16} strokeWidth={1.75} className="cf-search-icon" aria-hidden="true" />
          <input id="cf-search-input" type="search" placeholder="Search courses" value={search} onChange={e => setSearch(e.target.value)} className="cf-search-input" />
        </div>

        <FilterDropdown id="category" title="Category" badge={selectedCats.size || null} wide open={openFilter === 'category'} onToggle={() => toggleGroup('category')}>
          {ACADEMIES.map(a => {
            const Icon  = a.icon;
            const count = courses.filter(c => academySlugFor(c.id) === a.slug).length;
            const label = a.name.replace(' Academy', '');
            return (
              <label key={a.slug} className="cf-checkbox" title={label}>
                <input type="checkbox" checked={selectedCats.has(a.slug)} onChange={() => toggleCat(a.slug)} />
                <Icon size={12} strokeWidth={1.75} className="cf-checkbox-icon" aria-hidden="true" />
                <span className="cf-checkbox-label">{label}</span>
                <span className="cf-count">{count}</span>
              </label>
            );
          })}
        </FilterDropdown>

        <FilterDropdown id="difficulty" label="Difficulty" title={difficulty === 'all' ? 'Difficulty' : difficulty} badge={difficulty === 'all' ? null : 1} open={openFilter === 'difficulty'} onToggle={() => toggleGroup('difficulty')}>
          {['all', 'Beginner', 'Intermediate', 'Advanced'].map(level => (
            <label key={level} className="cf-radio">
              <input type="radio" name="difficulty" checked={difficulty === level} onChange={() => { setDifficulty(level); setOpenFilter(null); }} />
              <span className="cf-checkbox-label">{level === 'all' ? 'All Levels' : level}</span>
            </label>
          ))}
        </FilterDropdown>

        <FilterDropdown id="duration" title="Duration" badge={durationBuckets.size || null} open={openFilter === 'duration'} onToggle={() => toggleGroup('duration')}>
          {DURATION_BUCKETS.map(([key, label]) => (
            <label key={key} className="cf-checkbox">
              <input type="checkbox" checked={durationBuckets.has(key)} onChange={() => toggleDurationBucket(key)} />
              <span className="cf-checkbox-label">{label}</span>
            </label>
          ))}
        </FilterDropdown>

        {(q || selectedCats.size > 0 || difficulty !== 'all' || durationBuckets.size > 0) && (
          <button type="button" className="cf-clear" onClick={clearFilters}><X size={13} strokeWidth={2} aria-hidden="true" /> Clear filters</button>
        )}
        {themeGroups.length > 1 && (
          <nav className="catalogue-themes" aria-label="Jump to a theme">
            {themeGroups.map(g => {
              const Icon = g.icon;
              return (
                <button key={g.slug} type="button" className="catalogue-theme-chip" onClick={() => jumpToTheme(g.slug)} aria-label={`${g.name.replace(' Academy', '')}, ${g.courses.length} courses. Jump to this group`}>
                  <Icon size={12} strokeWidth={1.75} aria-hidden="true" /> {g.name.replace(' Academy', '')} <span>{g.courses.length}</span>
                </button>
              );
            })}
          </nav>
        )}
        <span className="catalogue-count" role="status" aria-live="polite">
          {visibleCourses.length === courses.length ? `${courses.length} courses across ${ACADEMIES.length} academies` : `${visibleCourses.length} of ${courses.length} courses`}
        </span>
      </div>

      <div className="catalogue-results">
          {isComingSoon ? (
            isAdmin ? (
              <div className="academy-coming-soon-admin">
                <div className="academy-coming-soon">
                  <p className="academy-coming-title">Coming Soon</p>
                  <p>Courses for {soloAcademy.name} are currently in development.</p>
                  {waitlist.length > 0 && (
                    <span className="academy-waitlist-badge">{waitlist.length} learner{waitlist.length !== 1 ? 's' : ''} waiting</span>
                  )}
                </div>
                {waitlist.length > 0 && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '8px 0' }}>
                      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        {waitlist.length} learner{waitlist.length !== 1 ? 's' : ''} waiting
                      </span>
                      {notifySent[soloAcademy.slug] != null ? (
                        <span style={{ fontSize: 13, color: 'var(--ok-status-done)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }} role="status">
                          <Check size={14} strokeWidth={1.75} aria-hidden="true" /> Notified {notifySent[soloAcademy.slug]}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={notifySending}
                          onClick={() => sendWaitlistNotification(soloAcademy.slug, soloAcademy.name)}
                        >
                          {notifySending ? 'Sending…' : `Notify all ${waitlist.length} when courses are live`}
                        </button>
                      )}
                    </div>
                    <table className="academy-waitlist-table">
                      <caption className="sr-only">Learners waiting for {soloAcademy.name}</caption>
                      <thead>
                        <tr><th scope="col">Name</th><th scope="col">Email</th><th scope="col">Company</th><th scope="col">Subscribed</th></tr>
                      </thead>
                      <tbody>
                        {waitlist.map(w => (
                          <tr key={w.id}>
                            <td>{w.name}</td>
                            <td style={{ color: 'var(--text-muted)' }}>{w.email}</td>
                            <td style={{ color: 'var(--text-muted)' }}>{w.company || 'Not provided'}</td>
                            <td style={{ color: 'var(--text-muted)' }}>{new Date(w.since).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            ) : (
              <div className="academy-coming-soon">
                <p className="academy-coming-title">Coming Soon</p>
                <p>Courses for {soloAcademy.name} are currently in development.</p>
                {notified.has(soloAcademy.slug) && <StatusChip kind="done">On the waitlist</StatusChip>}
                <button
                  type="button"
                  className={`btn ${notified.has(soloAcademy.slug) ? 'btn-ghost' : 'btn-primary'} academy-notify-btn`}
                  onClick={() => toggleNotify(soloAcademy.slug)}
                >
                  {notified.has(soloAcademy.slug) ? <>Leave the waitlist</> : 'Notify me when courses launch'}
                </button>
              </div>
            )
          ) : visibleCourses.length === 0 ? (
            <div className="catalogue-empty"><p>No courses match your filters.</p><button type="button" className="btn btn-ghost" onClick={clearFilters}>Clear filters</button></div>
          ) : (
            <div className="catalogue-groups">
              {themeGroups.map(g => {
                const Icon = g.icon;
                return (
                  <section key={g.slug} id={`theme-${g.slug}`} className="catalogue-group" aria-labelledby={`theme-title-${g.slug}`}>
                    <h3 id={`theme-title-${g.slug}`} className="catalogue-group-title">
                      <Icon size={16} strokeWidth={1.75} aria-hidden="true" /> {g.name} <span className="catalogue-group-count">{g.courses.length} course{g.courses.length === 1 ? '' : 's'}</span>
                    </h3>
                    <div className="catalogue-grid">
                      {g.courses.map(c => (
                        <CourseCard key={c.id} course={c} user={user} isAdmin={isAdmin}
                          enrolled={enrollments.has(c.id)} progress={progress} enroll={enroll}
                          navigateTo={navigateTo} myRating={myRatings[c.id] || 0} onReviewed={handleReviewed} />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}

function calcPct(course, progress) {
  const all  = course.modules?.flatMap(m => m.lessons?.map(l => l.id) || []) || [];
  const done = all.filter(id => progress[id]);
  return all.length ? Math.round(done.length / all.length * 100) : 0;
}

// Sends a paid-course access request to the admin Feedback Inbox.
function RequestAccessButton({ course, user }) {
  const key = `okiru-access-request:${user?.id}:${course.id}`;
  const [state, setState] = React.useState(() => { try { return localStorage.getItem(key) ? 'sent' : 'idle'; } catch { return 'idle'; } });
  const request = async () => {
    if (!user) return;
    setState('sending');
    try {
      const j = await fetch('/api/feedback', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId:crypto.randomUUID(),userId: user.id, userName: user.name, contextType: 'access-request', contextId: course.id,
          contextLabel: `Access request: ${course.title}`,
          message: `${user.name} (${user.email || 'no email'}${user.company ? `, ${user.company}` : ''}) has requested access to the paid course "${course.title}".`,
        }),
      }).then(r => r.json());
      if (!j.success) throw new Error();
      try { localStorage.setItem(key, new Date().toISOString()); } catch { /* storage unavailable */ }
      setState('sent');
    } catch {
      setState('error');
    }
  };
  if (state === 'sent') return <p className="cc-locked-pill cc-request-sent" role="status"><Check size={13} strokeWidth={2} aria-hidden="true" /> Access requested. Okiru will be in touch.</p>;
  return (
    <>
      <button type="button" className="btn btn-primary cc-btn" onClick={request} disabled={state === 'sending'}>
        <Lock size={13} strokeWidth={2} aria-hidden="true" /> {state === 'sending' ? 'Sending…' : state === 'error' ? 'Try again: request access' : 'Request access'}<span className="sr-only">: {course.title}</span>
      </button>
      <span role="alert" className={state === 'error' ? 'cc-error' : 'sr-only'}>{state === 'error' ? 'The request could not be sent.' : ''}</span>
    </>
  );
}

export function CourseCard({ course, user, isAdmin, enrolled, progress, enroll, navigateTo, myRating, onReviewed, showReviewPrompt = true }) {
  const dot       = categoryFill(course.category);
  const pct       = calcPct(course, progress);
  const firstMod  = course.modules?.[0];
  const completed = enrolled && pct === 100;
  const titleId   = `cc-title-${course.id}`;
  const unlockDate = course.availableFrom ? new Date(course.availableFrom) : null;
  const unlockText = unlockDate && !isNaN(unlockDate)
    ? `Unlocks ${unlockDate.toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })} at ${unlockDate.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`
    : 'Locked';
  const prereqTitle = course.prerequisiteCourseTitle || 'Claude for Professionals';

  // One status chip: icon plus word, never colour alone.
  let status = null;
  if (isAdmin) status = null;
  else if (course.paidLocked && !enrolled) status = <StatusChip kind="neutral">Paid, access on request</StatusChip>;
  else if (course.locked && course.lockReason === 'prerequisite') status = <StatusChip kind="pending">Locked</StatusChip>;
  else if (course.locked) status = <StatusChip kind="pending">{unlockText}</StatusChip>;
  else if (completed) status = <StatusChip kind="done">Completed</StatusChip>;
  else if (enrolled && pct > 0) status = <StatusChip kind="neutral">In progress, {pct}%</StatusChip>;
  else if (enrolled) status = <StatusChip kind="neutral">Enrolled</StatusChip>;

  return (
    <article className="course-card" aria-labelledby={titleId}>
      <CourseCover courseId={course.id} title={course.title} category={course.category} icon={course.coverIcon} decorative />
      <div className="cc-body">
        <p className="cc-area">
          <span className="cc-dot" style={{ background: dot }} aria-hidden="true" />
          <span>{course.category}</span>
        </p>
        <h4 className="cc-title" id={titleId}>{course.title}</h4>
        <ul className="cc-meta">
          <li>{course.level}</li>
          <li>{course.duration}</li>
          <li>{course.lessonCount} lessons</li>
          {course.delivery && <li>{course.delivery === 'classroom' ? 'Classroom' : course.delivery === 'blended' ? 'Self-paced and classroom' : 'Self-paced'}</li>}
          {course.enrolment === 'paid' && <li>Paid course</li>}
        </ul>
        {course.featured && <p className="cc-note" title={course.featuredReason}><Sparkles size={12} strokeWidth={2} aria-hidden="true" /> Recommended start</p>}
        <p className="cc-desc">{course.description}</p>
        {course.instructor?.name && <p className="cc-instructor">{course.instructor.name}</p>}
        <Stars rating={course.rating || 0} count={course.reviews || 0} />
        {status && <div className="cc-status">{status}</div>}
        {enrolled && pct > 0 && !completed && <ProgressBar value={pct} label={`Progress in ${course.title}`} />}
        {enrolled && !isAdmin && showReviewPrompt && (
          <InteractiveStars
            courseId={course.id}
            userId={user?.id}
            existingRating={myRating}
            onReviewed={onReviewed}
          />
        )}
      </div>
      <div className="cc-footer">
        {isAdmin ? (
          <div className="cc-btn-row">
            <button type="button" className="btn btn-ghost cc-btn" onClick={() => navigateTo('admin', course.id)}>
              View analytics<span className="sr-only"> for {course.title}</span>
            </button>
            <button type="button" className="btn btn-outline cc-btn" onClick={() => navigateTo('course-edit', null, null, course.id)}>
              Edit course<span className="sr-only">: {course.title}</span>
            </button>
          </div>
        ) : course.paidLocked && !enrolled ? (
          <RequestAccessButton course={course} user={user} />
        ) : course.locked && course.lockReason === 'prerequisite' ? (
          <p className="cc-locked-pill">Finish "{prereqTitle}" first to unlock this course.</p>
        ) : course.locked ? (
          <p className="cc-locked-pill">This course is not open yet.</p>
        ) : !enrolled ? (
          <button type="button" className="btn btn-primary cc-btn" onClick={() => { enroll(course.id); navigateTo('lesson', course.id, firstMod?.id); }}>
            Enrol and start<span className="sr-only">: {course.title}</span>
          </button>
        ) : completed ? (
          <div className="cc-btn-row">
            <button type="button" className="btn btn-ghost cc-btn" onClick={() => navigateTo('lesson', course.id, firstMod?.id)}>Revisit<span className="sr-only">: {course.title}</span></button>
            <button type="button" className="btn btn-outline cc-btn" onClick={() => navigateTo('certificates')}>Certificate<span className="sr-only"> for {course.title}</span></button>
          </div>
        ) : (
          <button type="button" className="btn btn-primary cc-btn" onClick={() => navigateTo('lesson', course.id, firstMod?.id)}>
            {pct > 0 ? 'Continue' : 'Start learning'}<span className="sr-only">: {course.title}</span>
          </button>
        )}
      </div>
    </article>
  );
}
